import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { outletService } from '../../services/outletService';
import { OutletDTO } from '../../types';
import { ArrowLeft, SquarePen, Trash2, Store, MapPin } from 'lucide-react';
import '../../modules/restaurant/pages/RestaurantPage.css';

const OutletDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState<OutletDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadItem = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await outletService.getById(parseInt(id));
      const d = (res.data as any)?.data ?? res.data;
      setItem(d);
    } catch {
      setError(t('restaurant.outlet.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    if (!window.confirm(t('restaurant.outlet.deleteConfirm'))) return;
    setDeleting(true);
    try {
      await outletService.delete(item.id);
      navigate('/restaurant/outlets');
    } catch {
      setError(t('restaurant.outlet.failedToDelete'));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="ims-page rest-page" style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="ims-page rest-page">
          <div className="ims-alert ims-alert-error">{error ?? t('restaurant.outlet.notFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="ims-detail-page rest-detail-page">
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="ims-hero rest-hero">
          <div className="ims-hero-nav">
            <button className="ims-hero-back" onClick={() => navigate('/restaurant/outlets')}>
              <ArrowLeft size={14} />
              {t('restaurant.outlet.title')}
            </button>
          </div>
          <div className="ims-hero-content">
            <div>
              <h1 className="ims-hero-number">{item.name}</h1>
              <p className="ims-hero-supplier">
                {item.code}
                {item.warehouseName && ` · ${item.warehouseName}`}
              </p>
              <div className="ims-hero-badges">
                <span className={`ims-hero-badge ${item.isActive ? 'rest-badge-active' : 'rest-badge-inactive'}`}>
                  {item.isActive ? t('common.active') : t('common.inactive')}
                </span>
              </div>
            </div>
            <div className="ims-hero-meta">
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="ims-btn ims-btn-secondary"
                  onClick={() => navigate(`/restaurant/outlets/${item.id}/edit`)}
                >
                  <SquarePen size={14} style={{ marginInlineEnd: 4 }} />
                  {t('common.edit')}
                </button>
                <button className="btn-delete" onClick={handleDelete} disabled={deleting}>
                  <Trash2 size={14} style={{ marginInlineEnd: 4 }} />
                  {deleting ? t('common.deleting') : t('common.delete')}
                </button>
              </div>
              <span className="ims-hero-date">
                {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBlock: '1rem' }}>
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        {/* ── Body ─────────────────────────────────────────────────────── */}
        <div className="ims-detail-body">
          <div className="ims-detail-main">
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">
                  <Store size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                  {t('restaurant.outlet.details')}
                </span>
              </div>
              <div className="ims-card-body">
                <div className="rest-fields-grid">
                  <div>
                    <div className="rest-field-label">{t('restaurant.outlet.code')}</div>
                    <div className="rest-field-value mono">{item.code}</div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.outlet.name')}</div>
                    <div className="rest-field-value strong">{item.name}</div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('common.status')}</div>
                    <div className="rest-field-value">
                      <span className={`ims-badge ${item.isActive ? 'rest-badge-active' : 'rest-badge-inactive'}`}>
                        {item.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.outlet.warehouse')}</div>
                    <div className="rest-field-value">{item.warehouseName || '—'}</div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.outlet.phone')}</div>
                    <div className="rest-field-value">{item.phone || '—'}</div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.outlet.email')}</div>
                    <div className="rest-field-value">{item.email || '—'}</div>
                  </div>
                  <div className="rest-field-full">
                    <div className="rest-field-label">{t('restaurant.outlet.address')}</div>
                    <div className="rest-field-value">{item.address || '—'}</div>
                  </div>
                  <div className="rest-field-full">
                    <div className="rest-field-label">{t('common.description')}</div>
                    <div className="rest-field-value">{item.description || '—'}</div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('common.createdAt')}</div>
                    <div className="rest-field-value">
                      {item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="ims-detail-sidebar">
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">
                  <MapPin size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                  Location
                </span>
              </div>
              <div className="ims-card-body">
                <div>
                  <div className="rest-field-label">{t('restaurant.outlet.address')}</div>
                  <div className="rest-field-value" style={{ marginBlockEnd: '1rem' }}>
                    {item.address || '—'}
                  </div>
                  <div className="rest-field-label">{t('restaurant.outlet.warehouse')}</div>
                  <div className="rest-field-value">
                    {item.warehouseName || '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OutletDetailPage;
